import {useCallback} from "react";
import {useModel23RegistryQuery} from "./query";
import yetiz from "yetiz";
import {useTranslation} from "react-i18next";


const emptyNizivProfile = () => ({
    name: "StationName",
    profiles: [
        {
            date: new Date().toISOString(),
            features: []
        }

    ]
})

const getTestTop = (hs, tdh, depth) => {
    if (!!hs && !!tdh && tdh > hs) {
        return tdh - parseInt(depth);
    } else if (hs !== undefined) {
        return hs - parseInt(depth);
    } else {
        return 0;
    }
}

const getStratigraphicProfile = (profile, yetiz, grainShapePrimaryMapper, grainShapeSecondaryMapper, RMapper) => {
    const layers = profile.stratigraphicProfile.map(l => ({
        ...l,
        top: parseInt(l.top),
        bottom: parseInt(l.bottom),
        grainSizeAvg: parseFloat(l.grainSizeAvg),
        grainSizeMax: parseFloat(l.grainSizeMax),
        hardness: RMapper[l.hardness] ?? undefined, //yetiz.Value.Hardness.index[l.R] ?? undefined,
        grainShapePrimary: grainShapePrimaryMapper[l.grainShapePrimary] ?? undefined,
        grainShapeSecondary: grainShapeSecondaryMapper[l.grainShapeSecondary] ?? undefined,
    }));

    // Keep the layers that have a valid top and bottom
    return layers.filter(l => !isNaN(l.top) && !isNaN(l.bottom));
}

const encodeNizivWetness = (stratigraphyLayers) => {
    if (stratigraphyLayers.length === 0) {
        return undefined;
    }
    return {
        type: "wetness",
        elements: [
            {
                meta: {},
                layers: stratigraphyLayers.map(
                    measurements => {
                        return {
                            top: measurements.top,
                            bottom: measurements.bottom,
                            value: measurements.wetness === "0" ? null : measurements.wetness  // 0 is not a valid yetiz code, it means NOT_DETECTABLE
                        }
                    }
                )
            }
        ]
    }
}

const encodeNizivHardness = (stratigraphyLayers) => {
    if (stratigraphyLayers.length === 0) {
        return undefined;
    }
    return {
        type: "hardness",
        elements: [
            {
                meta: {},
                layers: stratigraphyLayers.map(
                    measurements => {
                        return {
                            top: measurements.top,
                            bottom: measurements.bottom,
                            value: measurements.hardness
                        }
                    }
                )
            }
        ]
    }
}

const encodeNizivGrainShape = (stratigraphyLayers) => {
    if (stratigraphyLayers.length === 0) {
        return undefined;
    }
    return {
        type: "grainshape",
        elements: [
            {
                meta: {},
                layers: stratigraphyLayers.map(
                    measurements => {
                        return {
                            top: measurements.top,
                            bottom: measurements.bottom,
                            value: {
                                primary: measurements.grainShapePrimary,
                                secondary: measurements.grainShapeSecondary,
                                min: measurements.fg2,
                            }
                        }
                    }
                )
            }
        ]
    }
}

const encodeNizivGrainSize = (stratigraphyLayers) => {
    if (stratigraphyLayers.length === 0) {
        return undefined;
    }
    return {
        type: "grainsize",
        elements: [
            {
                meta: {},
                layers: stratigraphyLayers.map(
                    measurements => {
                        return {
                            top: measurements.top,
                            bottom: measurements.bottom,
                            value: {
                                avg: measurements.grainSizeAvg,
                                max: measurements.grainSizeMax,
                            }
                        }
                    }
                )
            }
        ]
    }
}

const encodeNizivComments = (stratigraphyLayers) => {
    if (stratigraphyLayers.length === 0) {
        return undefined;
    }
    return {
        type: "comments",
        elements: [
            {
                meta: {},
                layers: stratigraphyLayers.map(
                    measurements => {
                        return {
                            top: measurements.top,
                            bottom: measurements.bottom,
                        }
                    }
                )
            }
        ]
    }
}

const encodeNizivDensityProfiles = (densityProfiles) => {
    if (densityProfiles.length === 0) {
        return undefined;
    }
    return {
        type: "density",
        elements: densityProfiles.map(p => (
            {
                meta: {},
                layers: p.measurements.map(
                    m => {
                        return {
                            top: parseInt(m.top),
                            bottom: parseInt(m.bottom),
                            value: parseFloat(m.density)
                        }
                    }
                ).filter(m => !isNaN(m.top) && !isNaN(m.bottom))
            }
        ))
    }
}

const encodeYetizTemperatureProfiles = (temperatureProfiles) => {
    if (temperatureProfiles.length === 0) {
        return undefined;
    }
    return {
        type: "temperature",
        elements: temperatureProfiles.map(p => (
            {
                meta: {},
                layers: p.measurements.map(
                    m => {
                        return {
                            top: parseInt(m.depth),
                            bottom: parseInt(m.depth),
                            value: parseFloat(m.temperature)
                        }
                    }
                ).filter(m => !isNaN(m.top) && !isNaN(m.bottom))
            }
        ))
    }

}

const encodeNizivRamTests = (ramTests) => {
    if (ramTests.length === 0) {
        return undefined;
    }
    let prevDepth = 0;
    return {
        type: "ramm",
        elements: ramTests.map(t => ({
            meta: {pointprofile: false, comment: "", method: "Ram Sonde"},
            layers: t.measurements.map(m => {
                const value = {
                    value: {
                        depth: prevDepth,
                        height: m.depth - prevDepth,
                        value: m.hardness,
                    }
                }
                prevDepth = m.depth;
                return value;
            }).filter(({value}) => value.height !== 0)  // Filter out the layer with a height of 0, cause yetiz cannot handle it in the plot
        }))
    }
}

const encodeYetizLayerComments = (stratigraphyLayers) => {
    if (stratigraphyLayers.length === 0) {
        return undefined;
    }
    return {
        type: "comments",
        elements: [
            {
                meta: {"pointprofile": false},
                layers: stratigraphyLayers.map(
                    measurements => {
                        return {
                            top: measurements.top,
                            bottom: measurements.bottom,
                            value: measurements.notes
                        }
                    }
                )
            }
        ]
    }
}

const encodeExtendedColumnTests = (ectTests, hs) => {
    if (ectTests.length === 0) {
        return undefined;
    }
    return {
        type: "ect",
        elements: ectTests.map(t => (
            {
                meta: {"pointprofile": false},
                layers: t.measurements.map(m => ({
                    top: getTestTop(hs, m.tdh, m.depth),
                    value: {
                        value: m.score,
                        character: "",
                        comment: "",
                        nofailure: false,
                        customdata: {
                            ectcode: m.ectCode ?? "",
                            slope: m.slope ?? "",
                            tdh: m.tdh ?? null,
                            fracturetype: m.fractureType ?? "",
                            fracturesubtype: m.qualityShear ?? "",
                        }
                    },

                })).filter(m => !isNaN(m.top))
            }
        ))
    }
}

const encodedCompressionTest = (ctTests, hs) => {
    if (ctTests.length === 0) {
        return undefined;
    }
    return {
        type: "ct",
        elements: ctTests.map(t => (
            {
                meta: {"pointprofile": false},
                layers: t.measurements.map(m => ({
                    top: getTestTop(hs, m.tdh, m.depth),
                    value: {
                        value: m.score,
                        character: "",
                        comment: "",
                        nofailure: false,
                        customdata: {
                            ectcode: m.ectCode ?? "",
                            slope: m.slope ?? "",
                            tdh: m.tdh ?? null,
                            fracturetype: m.fractureType ?? "",
                            fracturesubtype: m.qualityShear ?? "",
                        }
                    }
                })).filter(m => !isNaN(m.top))
            }
        ))
    }
}

const encodedRutschBlockTests = (rbTests, hs) => {
    if (rbTests.length === 0) {
        return undefined;
    }
    return {
        type: "rb",
        elements: rbTests.map(t => (
            {
                meta: {"pointprofile": false},
                layers: t.measurements.map(m => ({
                    top: getTestTop(hs, m.tdh, m.depth),
                    value: {
                        character: "",
                        releasetype: "",
                        value: m.score,
                        customdata: {
                            slope: m.slope ?? "",
                            tdh: m.tdh ?? null,
                            rbcode: m.rbCode ?? "",
                            fracturetype: m.fractureType ?? "",
                            fracturesubtype: m.qualityShear ?? ""
                        }
                    }
                })).filter(m => !isNaN(m.top))
            }
        ))
    }
}

const encodedPropagationSawTest = (sawTests, hs) => {
    if (sawTests.length === 0) {
        return undefined;
    }
    return {
        type: "saw",
        elements: sawTests.map(t => (
            {
                meta: {"pointprofile": false},
                layers: [{
                    top: getTestTop(hs, t.tdh, t.depth),
                    value: {
                        "cutlength": t.cutLength,
                        "columnlength": t.columnLength,
                        "value": "Arr",  // This must be present and has to be a valid yetiz code [Arr | End | SF]
                        customdata: {
                            slope: t.slope ?? "",
                            tdh: t.tdh ?? null,
                            pstcode: t.pstCode ?? "",
                            fracturetype: t.fractureType ?? "",
                        }
                    }
                }].filter(m => !isNaN(m.top))
            }
        ))

    }
}


const toNizivJSONProfile = (profile, registry, translateAspect) => {
    const yetizProfile = emptyNizivProfile();

    const stationName = `${profile.station?.name ?? "StationName"} - ${profile.station?.code ?? "StationCode"}`

    yetizProfile.profiles[0].date = profile.datetime ?? new Date().toISOString()
    yetizProfile.name = stationName;
    yetizProfile.id = profile.station?.code ?? "";
    yetizProfile.profiles[0].top = 0;
    yetizProfile.profiles[0].bottom = undefined;

    yetizProfile.position = {
        latitude: profile.geom.coordinates?.[1],
        longitude: profile.geom.coordinates?.[0],
        altitude: profile.elevation,
        _aspect: profile.aspect ? translateAspect('enums:aspect.' + profile.aspect) : undefined,
        angle: profile.slope ?? '',
        description: profile.locationName ?? "",
    }

    yetizProfile.profiles[0].info = {
        observer: profile.observers,
        ta: profile.snowWeatherData.ta,
    }

    if(registry && yetiz){
        const grainShapes = registry.stratigraphicProfileRegistry.grainShapes[1].shapes;
        const grainShapePrimaryMapper = grainShapes.reduce((acc, item) => {
            acc[item.subClassId] = item.textSymbol ?? undefined;
            return acc;
        }, {});

        const grainShapeSecondaryMapper = grainShapes.reduce((acc, item) => {
            acc[item.subClassId] = item.textSymbol ?? undefined;
            return acc;
        }, {});

        const RMapper = registry.stratigraphicProfileRegistry.hardness.reduce((acc, item) => {
            acc[item.code] = item.codeNiviz ?? undefined;
            return acc;
        }, {});

        const nMapper = registry.snowWeatherDataRegistry.n.reduce((acc, item) => {
            acc[item.code] = item.observation ?? undefined;
            return acc;
        })

        const wwMapper = registry.snowWeatherDataRegistry.ww.reduce((acc, item) => {
            acc[item.code] = item.observation ?? undefined;
            return acc;
        })

        const vq1Mapper = registry.snowWeatherDataRegistry.vq1.reduce((acc, item) => {
            acc[item.code] = item.observation ?? undefined;
            return acc;
        });

        const vq2Mapper = registry.snowWeatherDataRegistry.vq2.reduce((acc, item) => {
            acc[item.code] = item.observation ?? undefined;
            return acc;
        });

        const stratigraphyLayers = getStratigraphicProfile(profile, yetiz, grainShapePrimaryMapper, grainShapeSecondaryMapper, RMapper);

        const top = parseInt(profile.snowWeatherData.hs);
        yetizProfile.profiles[0].top = top;
        yetizProfile.profiles[0].bottom = 0;
        yetizProfile.profiles[0].info.hs = top;

        yetizProfile.profiles[0].info.custom_meta = {
            precipitation: wwMapper[profile.snowWeatherData.ww] ?? "",
            wind: (vq1Mapper[profile.snowWeatherData.vq1] ?? "") + " - " + (vq2Mapper[profile.snowWeatherData.vq2] ?? ""),
            cloudiness: nMapper[profile.snowWeatherData.n] ?? "" ,
            notes: profile.notes ?? "",
            feedback: profile.feedback ?? "",
            swe: profile.swe,
            densityavg: (profile.swe && profile.snowWeatherData.hs) ? (profile.swe * 100 / (profile.snowWeatherData.hs)) : null,
        }

        // Generate all the features
        const features = [
            encodeNizivWetness(stratigraphyLayers),
            encodeNizivHardness(stratigraphyLayers),
            encodeNizivGrainShape(stratigraphyLayers),
            encodeNizivGrainSize(stratigraphyLayers),
            encodeNizivComments(stratigraphyLayers),
            encodeYetizTemperatureProfiles(profile.temperatureProfiles),
            encodeNizivDensityProfiles(profile.densityProfiles),
            encodeNizivRamTests(profile.ramTests),
            encodeYetizLayerComments(stratigraphyLayers),
            encodeExtendedColumnTests(profile.extendedColumnTests, top),
            encodedCompressionTest(profile.compressionTests, top),
            encodedRutschBlockTests(profile.rutschBlockTests, top),
            encodedPropagationSawTest(profile.propagationSawTests, top),
        ].filter(f => f !== undefined)

        // Add the features to the profile
        yetizProfile.profiles[0].features = features.map(f => f.type);
        features.forEach(f => {
            yetizProfile.profiles[0][f.type] = f;
        })
    }

    return JSON.stringify(yetizProfile);
}


const useYetizProfileEncoder = () => {
    const {t} = useTranslation(['enums']);
    const registryQuery = useModel23RegistryQuery();
    const registry = registryQuery.data;

    const encodeJSON = useCallback((profile) => {
        return toNizivJSONProfile(profile, registry, t);
    }, [registry])
    return {encodeJSON, isReady: !!registry };
}

export default useYetizProfileEncoder;